import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { fetchEmployeeBirthdayList } from "../actions/EventsAction";
import HeaderBannerPage from "../components/HeaderBannerPage";
import CakeIcon from "@mui/icons-material/Cake";
import CelebrationIcon from "@mui/icons-material/Celebration";

// Only these locations / statuses are eligible for the Events (Birthday) list
const ALLOWED_LOCATIONS = ["HAO", "ISM", "IIM"];
const ALLOWED_STATUS = "A";

const MONTHS = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

// "16-FEB-81" -> { day, month, label: "16 Feb" }
const parseBirthday = (birthday) => {
  if (!birthday) return null;
  const parts = String(birthday).split("-");
  if (parts.length < 2) return null;
  const day = parseInt(parts[0], 10);
  const month = MONTHS[parts[1]?.toUpperCase()];
  if (!day || !month) return null;
  const label = `${String(day).padStart(2, "0")} ${parts[1].charAt(0).toUpperCase()}${parts[1].slice(1).toLowerCase()}`;
  return { day, month, label };
};

const avatarUrl = (serviceNo) =>
  `https://esystems.cdl.lk/backend/AttendMe2/Home/GetDTSUserImg?serviceNo=${serviceNo}`;

const Events = () => {
  const dispatch = useDispatch();
  const { birthdayItems, loading } = useSelector((state) => state.events);

  useEffect(() => {
    dispatch(fetchEmployeeBirthdayList());
  }, [dispatch]);

  const employees = useMemo(() => {
    if (!birthdayItems || birthdayItems.length === 0) return [];

    const today = new Date();
    const todayMonth = today.getMonth() + 1;
    const todayDay = today.getDate();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

    return birthdayItems
      .filter(
        (emp) =>
          ALLOWED_LOCATIONS.includes(emp.EmployeeLocation) &&
          emp.EmployeeStatus === ALLOWED_STATUS
      )
      .map((emp) => ({ ...emp, __bday: parseBirthday(emp.EmployeeBirthday) }))
      // Only birthdays that fall today or later, within the current year
      .filter(
        (emp) =>
          emp.__bday &&
          (emp.__bday.month > todayMonth ||
            (emp.__bday.month === todayMonth && emp.__bday.day >= todayDay))
      )
      .map((emp) => {
        const occursOn = new Date(today.getFullYear(), emp.__bday.month - 1, emp.__bday.day).getTime();
        const daysUntil = Math.round((occursOn - todayMidnight) / (1000 * 60 * 60 * 24));
        return { ...emp, __daysUntil: daysUntil };
      })
      .sort((a, b) => a.__daysUntil - b.__daysUntil);
  }, [birthdayItems]);

  const featured = employees[0];
  const rest = employees.slice(1);

  const countdownText = (daysUntil) => {
    if (daysUntil === 0) return "Today";
    if (daysUntil === 1) return "Tomorrow";
    return `${daysUntil} days`;
  };

  return (
    <Box sx={{ padding: 0, margin: "0 auto" }}>
      <HeaderBannerPage />
      <Box
        sx={{
          px: { xs: "16px", sm: "26px" },
          pb: "28px",
          maxWidth: "480px",
          width: "100%",
          margin: "0 auto",
          mt: "6px",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: "20px" }}>
          <Typography
            sx={{
              fontSize: { xs: "15px", sm: "16px" },
              fontWeight: 700,
              color: "#154C79",
              textTransform: "uppercase",
              letterSpacing: "2px",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Birthday events
          </Typography>
          <Typography
            sx={{
              fontSize: "11.5px",
              color: "#9a9a9a",
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: "0.4px",
              mt: "6px",
            }}
          >
            {employees.length > 0
              ? `${employees.length} coming up in ${new Date().getFullYear()}`
              : new Date().getFullYear()}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
            <CircularProgress size={30} sx={{ color: "#E0A300" }} />
          </Box>
        ) : featured ? (
          <>
            {/* Featured "up next" hero card */}
            <Box
              sx={{
                position: "relative",
                borderRadius: "20px",
                overflow: "hidden",
                background: "linear-gradient(135deg,#154C79 0%,#0C3C6D 55%,#082B4E 100%)",
                padding: { xs: "22px 18px", sm: "26px 22px" },
                mb: "22px",
                boxShadow: "0px 10px 24px rgba(12,60,109,0.28)",
              }}
            >
              {/* decorative rings */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-40px",
                  right: "-30px",
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-50px",
                  left: "-40px",
                  width: "150px",
                  height: "150px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                }}
              />

              <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: "16px" }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: "72px", sm: "84px" },
                    height: { xs: "72px", sm: "84px" },
                    borderRadius: "50%",
                    padding: "3px",
                    background: "linear-gradient(135deg,#F2B807,#E0A300)",
                  }}
                >
                  <img
                    src={avatarUrl(featured.ServiceNo)}
                    alt={`User ${featured.EmployeeName}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #154C79",
                      display: "block",
                    }}
                    onError={(e) => (e.target.src = "/placeholder-image.jpg")}
                  />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      backgroundColor: "rgba(242,184,7,0.16)",
                      borderRadius: "20px",
                      padding: "3px 10px",
                      mb: "6px",
                    }}
                  >
                    <CelebrationIcon sx={{ fontSize: 13, color: "#F2CB5C" }} />
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#F2CB5C", letterSpacing: "0.5px" }}>
                      {featured.__daysUntil === 0 ? "TODAY" : "UP NEXT"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: { xs: "15px", sm: "17px" },
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {featured.EmployeeName}
                  </Typography>
                  {featured.EmployeeDesignation && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "rgba(255,255,255,0.65)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mt: "1px",
                      }}
                    >
                      {featured.EmployeeDesignation}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", mt: "10px" }}>
                    <Typography sx={{ fontSize: { xs: "19px", sm: "22px" }, fontWeight: 700, color: "#fff" }}>
                      {countdownText(featured.__daysUntil)}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.6)" }}>
                      · {featured.__bday.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Remaining list */}
            {rest.length > 0 && (
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#9a9a9a",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  mb: "10px",
                  ml: "2px",
                }}
              >
                Also coming up
              </Typography>
            )}

            <Box
              sx={{
                maxHeight: { xs: "calc(100vh - 460px)", sm: "420px" },
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(21,76,121,0.15)", borderRadius: "4px" },
              }}
            >
              {rest.map((item, index) => (
                <Box
                  key={item.ServiceNo || index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "46px minmax(0, 1fr) auto",
                    alignItems: "center",
                    columnGap: "12px",
                    padding: "10px 12px",
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                    border: "1px solid #EEF0F3",
                    mb: "8px",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={avatarUrl(item.ServiceNo)}
                    alt={`User ${item.EmployeeName}`}
                    style={{
                      width: "46px",
                      height: "46px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #F0F2F5",
                      display: "block",
                    }}
                    onError={(e) => (e.target.src = "/placeholder-image.jpg")}
                  />

                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontSize: "13px",
                        display: "block",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.EmployeeName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#999",
                        display: "block",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.EmployeeDesignation || item.ServiceNo}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#154C79" }}>
                      {item.__bday.label}
                    </Typography>
                    <Typography sx={{ fontSize: "10.5px", color: "#B4790A", fontWeight: 600 }}>
                      {countdownText(item.__daysUntil)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center", padding: "48px 16px" }}>
            <CakeIcon sx={{ fontSize: 38, color: "#e0e0e0", mb: 1 }} />
            <Typography sx={{ color: "#9a9a9a", fontSize: "13px", fontWeight: 500 }}>
              No more birthdays this year.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Events;